// Global Types
import type {
  $Translate,
} from 'common.language';
import type {
  $FullAdminRegatta,
  $FullRegattaAdminRequest,
  $Model,
  $ModelUpsertParams,
} from 'common.data';

// Global Enums
import {
  RegattaAdminRequestStatus,
} from 'common.data';

// Global Components
import * as React from 'react';
import {
  Button,
  Card,
  CardBody,
  Table,
} from 'reactstrap';
import {
  CommonDialog,
  DeleteDialog,
  FormDialog,
  Loader,
  SectionHeader,
} from '@vroskus/library-components';

// Helpers
import _ from 'lodash';
import {
  cleanFormValues,
  isDiff,
} from '@vroskus/library-helpers/dist/browser';
import {
  defaultDeleteItem,
  defaultGetData,
  defaultUpsertItem,
} from '../../../../helpers';

// Components
import RegattaAdminRequestFields from '../../../fields/RegattaAdminRequestFields';
import ThQty from '../../../common/ThQty';

// Services
import Services from '../../../../services';

// Types
import type {
  $ReducerState,
} from '../../../../redux';
import type {
  $RedirectAction,
} from '../../../../redux/modules/main/actions';

export type $OwnProps = $Translate & {
  id: string;
  item: $FullAdminRegatta;
};

export type $StateProps = {
  auth: $ReducerState<'auth'>;
};

export type $DispatchProps = {
  redirect: $RedirectAction;
};

type $Props = $DispatchProps & $OwnProps & $StateProps;

type $State = {
  data: Array<$Model<'Admin'>>;
  dataAdminRequests: Array<$FullRegattaAdminRequest>;
  loading: boolean;
};

class RegattaAdminsSectionView extends React.Component<$Props, $State> {
  constructor(props: $Props) {
    super(props);

    this.state = {
      data: [],
      dataAdminRequests: [],
      loading: false,
    };
  }

  componentDidMount(): void {
    this.getAdminData();
    this.getAdminRequestData();
    this.setupSocket();
  }

  componentDidUpdate(prevProps: $Props): void {
    const {
      item,
    } = this.props;

    if (isDiff(
      item.id,
      prevProps.item.id,
    )) {
      this.getAdminData();
      this.getAdminRequestData();
    }
  }

  setupSocket(): void {
    Services.Socket.on({
      action: (data) => {
        const {
          item,
        } = this.props;

        if (item.id === data.RegattaId && data.modelName === 'Admin') {
          this.getAdminData();
        }
      },
      type: Services.Socket.type.modelChangedEvent,
    });
    Services.Socket.on({
      action: (data) => {
        const {
          item,
        } = this.props;

        if (item.id === data.RegattaId && data.modelName === 'RegattaAdminRequest') {
          this.getAdminRequestData();
        }
      },
      type: Services.Socket.type.modelChangedEvent,
    });
  }

  setStateAsync(state: Partial<$State>): Promise<void> {
    return new Promise((resolve) => {
      this.setState(
        (pervState: $State) => ({
          ...pervState,
          ...state,
        }),
        resolve,
      );
    });
  }

  async getAdminData(): Promise<void> {
    const {
      item,
    } = this.props;

    const RegattaId = item.id;

    await defaultGetData({
      action: async () => {
        await this.setStateAsync({
          loading: true,
        });

        const data = await Services.Api.getManyRegattaAdmin({
          RegattaId,
        });

        await this.setStateAsync({
          data,
          loading: false,
        });
      },
      errorAction: async () => this.stopLoader(),
    });
  }

  async getAdminRequestData(): Promise<void> {
    const {
      item,
    } = this.props;

    const RegattaId = item.id;

    await defaultGetData({
      action: async () => {
        await this.setStateAsync({
          loading: true,
        });

        const dataAdminRequests = await Services.Api.getManyRegattaRegattaAdminRequest({
          RegattaId,
        });

        await this.setStateAsync({
          dataAdminRequests,
          loading: false,
        });
      },
      errorAction: async () => this.stopLoader(),
    });
  }

  async stopLoader(): Promise<void> {
    await this.setStateAsync({
      loading: false,
    });
  }

  renderHeader() {
    const {
      item,
      t,
    } = this.props;

    const RegattaId = item.id;

    const newItem: $ModelUpsertParams<'RegattaAdminRequest'> = {
      Email: '',
      Name: null,
      RegattaId,
      Status: RegattaAdminRequestStatus.request,
    };

    return (
      <SectionHeader
        bottomBorder
        description={t('A list of users that can control this regatta')}
        iconClass={'icon-lock'}
        rightButtons={(
          <FormDialog
            fields={(
              <RegattaAdminRequestFields item={newItem} />
            )}
            id={'create-regatta-admin-dialog'}
            labelCloseButton={t('Close')}
            labelSaveButton={t('Save')}
            onAction={(values) => defaultUpsertItem({
              action: async () => {
                const params = cleanFormValues<$Model<'RegattaAdminRequest'>>({
                  ...newItem,
                  ...values,
                });

                await Services.Api.upsertRegattaAdminRequest(params);
              },
            })}
            subTitle={t('New')}
            title={t('Regatta admin')}
          >
            <Button
              color={'info'}
              id={'create-regatta-admin-button'}
              size={'sm'}
              title={t('Add admin')}
            >
              {t('Add')}
            </Button>
          </FormDialog>
        )}
        title={t('Regatta admins')}
      />
    );
  }

  renderAdmins() {
    const {
      auth,
      item,
      redirect,
      t,
    } = this.props;
    const {
      data,
    } = this.state;

    const RegattaId = item.id;

    return (
      <>
        {data.map((dataItem: $Model<'Admin'>) => {
          const {
            id,
          } = dataItem;

          return (
            <tr key={id}>
              <td>{dataItem.Name}</td>
              <td>{dataItem.Email}</td>
              <td className={'text-right'}>
                <DeleteDialog
                  content={t('Do you really want to remove?')}
                  id={`delete-regatta-admin-dialog-${id}`}
                  labelCloseButton={t('Close')}
                  labelDeleteButton={t('Delete')}
                  onAction={() => defaultDeleteItem({
                    action: async () => {
                      await Services.Api.removeRegattaAdmin({
                        AdminId: id,
                        RegattaId,
                      });

                      if (dataItem.id === _.get(
                        auth,
                        'user.id',
                      )) {
                        redirect({
                          pathname: '/regattas',
                        });
                      }
                    },
                  })}
                  subTitle={_.get(
                    _.find(
                      data,
                      {
                        id,
                      },
                    ),
                    'Name',
                  ) || ''}
                  title={t('Regatta admin')}
                >
                  <Button
                    color={'danger'}
                    id={`delete-regatta-admin-button-${id}`}
                    outline
                    size={'sm'}
                  >
                    {t('Remove')}
                  </Button>
                </DeleteDialog>
              </td>
            </tr>
          );
        })}
      </>
    );
  }

  renderPendingAdmins() {
    const {
      t,
    } = this.props;
    const {
      dataAdminRequests,
    } = this.state;

    const filteredAdminRequests = _.filter(
      dataAdminRequests,
      ({
        Status,
      }) => Status !== RegattaAdminRequestStatus.fulfilled,
    );

    return (filteredAdminRequests.length > 0 ? (
      <>
        <tr>
          <td
            className={'text-center'}
            colSpan={100}
          >
            <h5 className={'mb-0 text-bold'}>
              {t('Pending regatta admin requests')}
            </h5>
          </td>
        </tr>
        {filteredAdminRequests.map((dataItem: $FullRegattaAdminRequest) => {
          const {
            id,
          } = dataItem;

          return (
            <tr key={id}>
              <td>{dataItem.Name}</td>
              <td>{dataItem.Email}</td>
              <td>
                <div className={'d-flex justify-content-end'}>
                  {RegattaAdminRequestStatus.propose === dataItem.Status && (
                    <CommonDialog
                      content={(
                        <CardBody>
                          {t('Do you really want to add this user to admins?')}
                        </CardBody>
                      )}
                      id={`confirm-regatta-admin-dialog-${id}`}
                      labelCloseButton={t('Close')}
                      labelConfirmButton={t('Confirm')}
                      onAction={() => defaultUpsertItem({
                        action: async () => {
                          const params = cleanFormValues<$FullRegattaAdminRequest>({
                            ...dataItem,
                            Status: RegattaAdminRequestStatus.confirmed,
                          });

                          await Services.Api.upsertRegattaAdminRequest(params);
                        },
                      })}
                      title={t('Need confirmation')}
                    >
                      <Button
                        color={'warning'}
                        id={`confirm-regatta-admin-button-${id}`}
                        outline
                        size={'sm'}
                      >
                        {t('Confirm')}
                      </Button>
                    </CommonDialog>
                  )}
                  {RegattaAdminRequestStatus.request === dataItem.Status && (
                    <DeleteDialog
                      content={t('Do you really want to revoke?')}
                      id={`revoke-regatta-admin-dialog-${id}`}
                      labelCloseButton={t('Close')}
                      labelDeleteButton={t('Delete')}
                      onAction={() => defaultDeleteItem({
                        action: async () => Services.Api.revokeRegattaAdminRequest(dataItem),
                      })}
                      subTitle={dataItem.Name || ''}
                      title={t('Regatta admin request')}
                    >
                      <Button
                        color={'danger'}
                        id={`revoke-regatta-admin-button-${id}`}
                        outline
                        size={'sm'}
                      >
                        {t('Revoke')}
                      </Button>
                    </DeleteDialog>
                  )}
                </div>
              </td>
            </tr>
          );
        })}
      </>
    ) : null);
  }

  renderTable() {
    const {
      t,
    } = this.props;
    const {
      data,
    } = this.state;

    return (
      <Table responsive>
        <thead>
          <tr>
            <th>{t('Name')}</th>
            <th>{t('Email')}</th>
            <ThQty value={data.length} />
          </tr>
        </thead>
        <tbody>
          {this.renderAdmins()}
          {this.renderPendingAdmins()}
        </tbody>
      </Table>
    );
  }

  renderContent() {
    const {
      loading,
    } = this.state;

    return (
      <Loader
        loading={loading === true}
        overlay
      >
        {this.renderTable()}
      </Loader>
    );
  }

  render() {
    const {
      id,
    } = this.props;

    return (
      <Card id={id}>
        {this.renderHeader()}
        {this.renderContent()}
      </Card>
    );
  }
}

export default RegattaAdminsSectionView;
